import { useCallback, useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { InputArea } from '~/components/common/InputArea'
import { Modal } from '~/components/modals/CurieModal'
import Typography from '~/components/common/Typography'
import { AccountType } from '~/constants/settings'
import { useBillingContext } from '~/contexts/BillingContext'
import { useModalContext } from '~/contexts/ModalContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { bridge } from '~/services/bridge'
import { ApiErrorType } from '~/utils/shared'
import { BetterToast } from '~/utils/toast'

interface AddWorkspaceModalProps {
  open: boolean
  handleClose: () => void
}

function AddWorkspaceModal({ open, handleClose }: AddWorkspaceModalProps) {
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const { createNewWorkspace } = useWorkspaceContext()
  const { showErrorAlert, openAlert } = useModalContext()
  const {
    hasLicense,
    billingInfo,
    loaded: billingLoaded,
    isBillingAdmin,
    permittedCommands
  } = useBillingContext()

  /**
   * Check if the account has a team tier or above license
   * Returns true if account has TEAM, ENTERPRISE, or EXEMPT license
   * Returns false otherwise (needs upgrade)
   */
  const hasTeamOrAboveLicense = useCallback(() => {
    return (
      hasLicense(AccountType.TEAM) ||
      hasLicense(AccountType.ENTERPRISE) ||
      hasLicense(AccountType.ENTERPRISE_MIGRATE) ||
      hasLicense(AccountType.ENTERPRISE_MIGRATE_ASSESS) ||
      hasLicense(AccountType.ENTERPRISE_MIGRATE_ACCELERATE) ||
      hasLicense(AccountType.ENTERPRISE_MIGRATE_REFACTOR) ||
      hasLicense(AccountType.EXEMPT)
    )
  }, [hasLicense])

  // Check if plan is below team (needs upgrade)
  const isPlanBelowTeam = useCallback(() => {
    return !hasTeamOrAboveLicense()
  }, [hasTeamOrAboveLicense])

  // Show upgrade modal and close workspace modal if plan is below team
  const showUpgradeModal = useCallback(() => {
    handleClose()
    showErrorAlert(
      ApiErrorType.UPGRADE_REQUIRED,
      'Creating workspaces is only available on Team plan or above. Please upgrade to create workspaces.'
    )
  }, [handleClose, showErrorAlert])

  // Show alert modal if user is not billing admin (no upgrade action needed)
  const showBillingAdminError = useCallback(() => {
    handleClose()
    openAlert({
      title: 'Billing Admin Required',
      message:
        'Only billing admins can create workspaces. Please contact your billing admin to create a workspace.'
      // No primaryAction - just shows Cancel button
    })
  }, [handleClose, openAlert])

  // When modal opens, check conditions and show appropriate modal/error
  useEffect(() => {
    if (!open || !billingLoaded) {
      return
    }

    // Check if billingInfo is available
    if (!billingInfo) {
      return
    }

    // Wait for permittedCommands to load before checking license
    // (permittedCommands.active_license_types is the reliable source for all users)
    if (!permittedCommands) {
      return
    }

    // First check if plan is below team - show upgrade modal immediately
    if (isPlanBelowTeam()) {
      showUpgradeModal()
      return
    }

    // Then check if user is billing admin - show alert modal if not
    if (!isBillingAdmin) {
      showBillingAdminError()
      return
    }

    // If we get here, user has permission - modal will show via shouldShowModal
  }, [
    open,
    billingLoaded,
    billingInfo,
    permittedCommands,
    isPlanBelowTeam,
    isBillingAdmin,
    showUpgradeModal,
    showBillingAdminError
  ])

  // Don't render the modal if:
  // 1. Billing not loaded yet or permittedCommands not loaded
  // 2. Plan is below team (upgrade modal will show instead)
  // 3. User is not billing admin (alert modal will show instead)
  const shouldShowModal =
    open &&
    billingLoaded &&
    !!billingInfo &&
    !!permittedCommands &&
    !isPlanBelowTeam() &&
    isBillingAdmin

  const handleSubmit = useCallback(async () => {
    // Safety check: if plan is below team, show upgrade modal
    if (isPlanBelowTeam()) {
      showUpgradeModal()
      return
    }

    // Safety check: if user is not billing admin, show error
    if (!isBillingAdmin) {
      showBillingAdminError()
      return
    }

    try {
      await createNewWorkspace(newWorkspaceName)
      handleClose()
    } catch (error: unknown) {
      // Backend returns structured errors with upgradeRequired flag
      const err = error as { upgradeRequired?: boolean; message?: string }
      if (err?.upgradeRequired) {
        showErrorAlert(
          ApiErrorType.UPGRADE_REQUIRED,
          err?.message ||
            'Creating multiple workspaces is only available on paid plans. Please upgrade to create more workspaces.'
        )
        handleClose()
      } else {
        const message =
          err?.message || (error instanceof Error ? error.message : 'Failed to create workspace')
        BetterToast.error(message)
        handleClose()
      }
    }
  }, [
    newWorkspaceName,
    createNewWorkspace,
    handleClose,
    showErrorAlert,
    isPlanBelowTeam,
    isBillingAdmin,
    showUpgradeModal,
    showBillingAdminError
  ])

  return (
    <Modal
      isOpen={shouldShowModal}
      onClose={handleClose}
      className={bridge.isInPlugin() ? 'w-full max-w-[calc(100vw-24px)]' : 'w-[972px] h-[622px]'}
    >
      <div className='flex flex-col h-full'>
        <div className='flex flex-col h-full pt-[60px] items-center gap-4 justify-between'>
          <div className='flex flex-col items-center p-2'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='25'
              height='24'
              viewBox='0 0 25 24'
              fill='none'
            >
              <path
                d='M17.5 20H22.5V18C22.5 16.3431 21.1569 15 19.5 15C18.5444 15 17.6931 15.4468 17.1438 16.1429M17.5 20H7.5M17.5 20V18C17.5 17.3438 17.3736 16.717 17.1438 16.1429M7.5 20H2.5V18C2.5 16.3431 3.84315 15 5.5 15C18.5444 15 17.30686 15.4468 17.85625 16.1429M7.5 20V18C7.5 17.3438 7.62642 16.717 7.85625 16.1429M7.85625 16.1429C8.5935 14.301 10.3948 13 12.5 13C14.6052 13 16.4065 14.301 17.1438 16.1429M15.5 7C15.5 8.65685 14.1569 10 12.5 10C10.8431 10 9.5 8.65685 9.5 7C9.5 5.34315 10.8431 4 12.5 4C14.1569 4 15.5 5.34315 15.5 7ZM21.5 10C21.5 11.1046 20.6046 12 19.5 12C18.3954 12 17.5 11.1046 17.5 10C17.5 8.89543 18.3954 8 19.5 8C20.6046 8 21.5 8.89543 21.5 10ZM7.5 10C7.5 11.1046 6.60457 12 5.5 12C4.39543 12 3.5 11.1046 3.5 10C3.5 8.89543 4.39543 8 5.5 8C6.60457 8 7.5 8.89543 7.5 10Z'
                stroke='#21272A'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
          <Typography
            fontFamily='geist'
            fontSize='titleLg'
            fontWeight='bold'
            className='text-black'
          >
            Create Workspace
          </Typography>
          <div className='flex flex-col items-center mt-[42px] p-[10px] w-[516px] h-[68px] rounded-[8px]'>
            <InputArea
              placeholder='Enter a name for your workspace'
              singleLine
              value={newWorkspaceName}
              onChange={e => setNewWorkspaceName(e.target.value)}
              className='w-full h-[48px]'
            />
          </div>
          <div className='h-full'></div>
          <div className='flex flex-col px-8 py-4 w-full h-[68px] border border-[#EAEAEA] rounded-[8px] items-end items-between'>
            <Button
              variant='primary'
              className='w-[165px] h-[48px]'
              isDisabled={!newWorkspaceName.trim()}
              onClick={handleSubmit}
            >
              Create Workspace
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default AddWorkspaceModal
